import type { ComponentProps } from 'react';
import { memo, useRef, useContext } from 'react';
import { TooltipStateLess, VStack, HStack } from '@hst-horizon/horizon-ui';
import withErrorBoundary from '../../../../../core/components/errors/withErrorBoundary';
import { AppContext } from '../../../../../core/context';
import Divider from '../../../../../core/components/Divider';
import type { SearchResultProduct } from '../../../../../product/queries/useProductSearch';
import styles from './SearchField.module.scss';
import { CategoriesSection, BrandsSection } from './SearchResults';
import ProductsResult from './ProductsResult';

const SearchDropdown = ({
    children,
    categories,
    brands,
    products,
    onOpenChange,
    isOpen,
    resetSearch,
    searchValue,
    submitSearch,
}: {
    onOpenChange?: (val: boolean) => void;
    children: ComponentProps<typeof TooltipStateLess>['children'];
    categories: {
        items: { id: string; name: string }[];
        loading: boolean;
        error?: Error;
    };
    brands: {
        items: { logo?: string; name: string; slug: string }[];
        loading: boolean;
        error?: Error;
    };
    products: {
        items: SearchResultProduct[];
        loading: boolean;
        error?: Error;
    };
    isOpen: boolean;
    resetSearch: () => void;
    searchValue: string;
    submitSearch(): void;
}) => {
    const ref = useRef(null);

    const { isInMobileView } = useContext(AppContext);

    return (
        <TooltipStateLess
            toggle={false}
            dismiss
            hasInput
            onOpenChange={onOpenChange}
            theme={styles.tooltipTheme}
            open={isOpen}
            mode="click"
            placement="bottom"
            matchReferenceWidth
            offsetOptions={
                isInMobileView
                    ? {
                          crossAxis: -10,
                      }
                    : undefined
            }
            content={
                <div ref={ref} className={styles.container}>
                    {isInMobileView ? (
                        <VStack gap="small" className={styles.mobileContent}>
                            <CategoriesSection
                                categories={categories.items}
                                isLoading={categories.loading}
                                error={categories.error}
                                onClick={resetSearch}
                            />
                            <Divider light lengthPercentage={100} />
                            <BrandsSection
                                brands={brands.items}
                                error={brands.error}
                                isLoading={brands.loading}
                                onClick={resetSearch}
                            />
                            <Divider light lengthPercentage={100} />
                            <ProductsResult
                                products={products.items}
                                error={products.error}
                                isLoading={products.loading}
                                search={searchValue}
                                onClick={resetSearch}
                                submitSearch={submitSearch}
                            />
                        </VStack>
                    ) : (
                        <HStack>
                            <VStack
                                gap="small"
                                className={styles.categoriesBrands}
                            >
                                <CategoriesSection
                                    categories={categories.items}
                                    isLoading={categories.loading}
                                    error={categories.error}
                                    onClick={resetSearch}
                                />
                                <BrandsSection
                                    brands={brands.items}
                                    error={brands.error}
                                    isLoading={brands.loading}
                                    onClick={resetSearch}
                                />
                            </VStack>
                            <ProductsResult
                                products={products.items}
                                error={products.error}
                                isLoading={products.loading}
                                search={searchValue}
                                onClick={resetSearch}
                                submitSearch={submitSearch}
                            />
                        </HStack>
                    )}
                </div>
            }
        >
            {children}
        </TooltipStateLess>
    );
};

export default memo(withErrorBoundary(SearchDropdown));
